import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import DashboardFilters from '../../../shared/components/ui/DashboardFilters';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getManagementPriorities,
  getAllUsersByProjectId,
  editProject,
  getFollowUpsByProjectId,
  getEngineerPrioritiesByProjectId,
  getInventoryDailyTasks,
  updateManagementPriorities
} from '../../services/api/implementation/impl';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import mixpanel from 'mixpanel-browser';
import Loader from '../../../shared/components/ui/Loader';
import Modal from '../../../shared/components/ui/Modal';
import FormInput from '../../../shared/components/ui/FormInput';
import Button from '../../../shared/components/ui/Button';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import sales from '../../../shared/assets/svg/sales.svg';
import operations from '../../../shared/assets/svg/operations.svg';
import inventory from '../../../shared/assets/svg/Inventory.svg';
import Multiselect from 'multiselect-react-dropdown';

const ManagementDashboard = () => {
  const { collapsed } = useSidebar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filters, setFilters] = useState({ projectID: null, month: null });
  const [isFiltersLoading, setIsFiltersLoading] = useState(false);
  const navigate = useNavigate();
  const userID = Number(Cookies.get('userId'));
  const [selectedDate, setSelectedDate] = useState(
    localStorage.getItem('engineerSelectedDate') || new Date().toISOString().split('T')[0]
  );
  const [showDatePicker, setShowDatePicker] = useState(false);

  // State for priority assignment modal
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [selectedPriorityType, setSelectedPriorityType] = useState(null);
  const [selectedPriorityProjectId, setSelectedPriorityProjectId] = useState(null);
  const [selectedPriorityId, setSelectedPriorityId] = useState(null); // Store priority ID
  const queryClient = useQueryClient();

  useEffect(() => {
    // Track page view
    mixpanel.track('Management Dashboard Viewed', {
      user_id: userID,
      timestamp: new Date().toISOString()
    });
  }, [userID]);

  const {
    data: priorities,
    refetch: refetchPriorities,
    isLoading: isLoadingPriorities
  } = useQuery({
    queryKey: ['priorities', filters.projectID],
    queryFn: () => getManagementPriorities(filters.projectID, null),
    // enabled: !!filters.projectID,
    staleTime: 0, // Data is considered stale immediately
    gcTime: 0, // Don't cache the data
    refetchOnMount: true, // Always refetch when component mounts
    refetchOnWindowFocus: false, // Don't refetch on window focus
    onSuccess: (data) => {
      mixpanel.track('Follow Ups API Success', {
        user_id: userID,
        project_id: filters.projectID,
        follow_ups_count: data?.data?.length || 0,
        timestamp: new Date().toISOString()
      });
    },
    onError: (error) => {
      mixpanel.track('Follow Ups API Failed', {
        user_id: userID,
        project_id: filters.projectID,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  // Fetch Sales Escalations
  const {
    data: salesEscalations,
    refetch: refetchSalesEscalations,
    isLoading: isLoadingSalesEscalations
  } = useQuery({
    queryKey: ['salesEscalations', filters.projectID],
    queryFn: () => getFollowUpsByProjectId(filters.projectID, null),
    enabled: !!filters.projectID,
    onSuccess: (data) => {
      mixpanel.track('Sales Escalations API Success', {
        user_id: userID,
        project_id: filters.projectID,
        escalations_count: data?.data?.length || 0,
        timestamp: new Date().toISOString()
      });
    },
    onError: (error) => {
      mixpanel.track('Sales Escalations API Failed', {
        user_id: userID,
        project_id: filters.projectID,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  // Fetch Engineer Escalations
  const {
    data: engineerEscalations,
    refetch: refetchEngineerEscalations,
    isLoading: isLoadingEngineerEscalations
  } = useQuery({
    queryKey: ['engineerEscalations', filters.projectID, selectedDate],
    queryFn: () => getEngineerPrioritiesByProjectId(filters.projectID, selectedDate),
    enabled: !!filters.projectID && !!selectedDate,
    onSuccess: (data) => {
      mixpanel.track('Engineer Escalations API Success', {
        user_id: userID,
        project_id: filters.projectID,
        escalations_count: data?.data?.length || 0,
        timestamp: new Date().toISOString()
      });
    },
    onError: (error) => {
      mixpanel.track('Engineer Escalations API Failed', {
        user_id: userID,
        project_id: filters.projectID,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  // Fetch Inventory Tasks
  const {
    data: inventoryTasks,
    refetch: refetchInventoryTasks,
    isLoading: isLoadingInventoryTasks
  } = useQuery({
    queryKey: ['inventoryTasks', filters.projectID, selectedDate],
    queryFn: () => getInventoryDailyTasks(filters.projectID, selectedDate),
    enabled: !!filters.projectID && !!selectedDate,
    onSuccess: (data) => {
      mixpanel.track('Inventory Tasks API Success', {
        user_id: userID,
        project_id: filters.projectID,
        tasks_count: data?.length || 0,
        timestamp: new Date().toISOString()
      });
    },
    onError: (error) => {
      mixpanel.track('Inventory Tasks API Failed', {
        user_id: userID,
        project_id: filters.projectID,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  const handleFiltersChange = useCallback(({ projectID, month }) => {
    setFilters({ projectID, month });
  }, []);

  const handleFiltersLoadingChange = useCallback((isLoading) => {
    setIsFiltersLoading(isLoading);
  }, []);

  // Fetch management users for assignment modal
  const { data: managementUsers, isLoading: isLoadingManagementUsers } = useQuery({
    queryKey: ['managementUsers', filters.projectID],
    queryFn: () => getAllUsersByProjectId(filters.projectID, 1, 1000000),
    enabled: showPriorityModal && !!filters.projectID
  });

  // Form for priority assignment
  const {
    register: registerPriority,
    handleSubmit: handleSubmitPriority,
    formState: { errors: priorityErrors },
    reset: resetPriority,
    watch: watchPriority,
    control: controlPriority
  } = useForm();

  const selectedUserId = watchPriority('userId');
  const selectedDay = watchPriority('day');
  const selectedAttendees = watchPriority('attendees') || [];

  // Mutation for updating management priority
  const { mutate: updatePriorityMutation, isPending: isUpdatingPriority } = useMutation({
    mutationFn: ({ id, payload }) => updateManagementPriorities(id, payload),
    onSuccess: () => {
      toast.success('Priority updated successfully');
      // Refetch priorities after successful update
      refetchPriorities();
      mixpanel.track('Management Priority Updated', {
        user_id: userID,
        priority_id: selectedPriorityId,
        timestamp: new Date().toISOString()
      });
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to update priority');
      mixpanel.track('Management Priority Update Failed', {
        user_id: userID,
        priority_id: selectedPriorityId,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  // Mutation for updating project
  const { mutate: updateProjectMutation, isPending: isUpdatingProject } = useMutation({
    mutationFn: (payload) => editProject(selectedPriorityProjectId, payload),
    onSuccess: async (_, payload) => {
      // After editProject succeeds, call updateManagementPriorities
      if (selectedPriorityId && payload) {
        updatePriorityMutation(
          { id: selectedPriorityId, payload: { status: 'Completed' } },
          {
            onSuccess: () => {
              // Invalidate and refetch priorities after priority update completes
              queryClient.invalidateQueries({ queryKey: ['priorities', filters.projectID] });
              refetchPriorities();
            }
          }
        );
      } else {
        // If no priority update needed, invalidate and refetch immediately
        queryClient.invalidateQueries({ queryKey: ['priorities', filters.projectID] });
        refetchPriorities();
      }

      setShowPriorityModal(false);
      resetPriority();
      setSelectedPriorityType(null);
      setSelectedPriorityProjectId(null);
      setSelectedPriorityId(null);
      mixpanel.track('Priority Assignment Updated', {
        user_id: userID,
        priority_type: selectedPriorityType,
        project_id: selectedPriorityProjectId,
        timestamp: new Date().toISOString()
      });
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to update project');
      mixpanel.track('Priority Assignment Failed', {
        user_id: userID,
        priority_type: selectedPriorityType,
        project_id: selectedPriorityProjectId,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  // Handle priority assignment form submission
  const onSubmitPriorityAssignment = (data) => {
    if (!selectedPriorityProjectId) {
      toast.error('Project ID is missing');
      return;
    }

    const payload = {};

    // Map priority type to payload key
    switch (selectedPriorityType) {
      case 'User Assign For CCR':
        payload.customerChangeRequestApprovalUser_id = parseInt(data.userId);
        break;
      case 'User Assign For Deal':
        payload.dealAppprovingUser_id = parseInt(data.userId);
        break;
      case 'User Assign For UC':
        payload.ucAppprovingUser_id = parseInt(data.userId);
        break;
      case 'Review Meeting Date':
        payload.reviewMeetingDate = parseInt(data.day);
        payload.organizerUser_id = parseInt(data.organizerUserId);
        // Extract user IDs from selected attendees array
        if (data.attendees && Array.isArray(data.attendees) && data.attendees.length > 0) {
          payload.attendees = data.attendees.map((attendee) =>
            typeof attendee === 'object' && attendee !== null
              ? parseInt(attendee.id)
              : parseInt(attendee)
          );
        } else {
          payload.attendees = [];
        }
        break;
      case 'Target Meeting Date':
        payload.targetMeetingDate = parseInt(data.day);
        payload.targetOrganizer_id = parseInt(data.targetOrganizerUserId);
        // Extract user IDs from selected target attendees array
        if (
          data.targetAttendees &&
          Array.isArray(data.targetAttendees) &&
          data.targetAttendees.length > 0
        ) {
          payload.targetAttendees = data.targetAttendees.map((attendee) =>
            typeof attendee === 'object' && attendee !== null
              ? parseInt(attendee.id)
              : parseInt(attendee)
          );
        } else {
          payload.targetAttendees = [];
        }
        break;
      default:
        toast.error('Invalid priority type');
        return;
    }

    // Store payload for use in updateManagementPriorities
    updateProjectMutation(payload);
  };

  // Check if priority type requires modal (not navigation)
  const requiresModal = (priorityType) => {
    return [
      'User Assign For CCR',
      'User Assign For Deal',
      'User Assign For UC',
      'Review Meeting Date',
      'Target Meeting Date'
    ].includes(priorityType);
  };

  // Close date picker when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDatePicker && !event.target.closest('.date-picker-container')) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker]);

  // Process Sales Escalations Data - return total count
  const getSalesEscalationsCount = (data) => {
    if (!data || !Array.isArray(data)) return 0;
    return data.filter((item) => item.isEscalated && item.status === 'Active').length;
  };

  // Process Engineer Escalations Data - return total count
  const getEngineerEscalationsCount = (data) => {
    if (!data || !Array.isArray(data)) return 0;
    return data.filter((item) => item.isEscalated && item.status !== 'Completed').length;
  };

  // Process Inventory Escalations Data - return total count
  const getInventoryEscalationsCount = (data) => {
    if (!data || !Array.isArray(data)) return 0;
    return data.filter((item) => item.status !== 'Completed').length;
  };

  // Check if a priority type is an approval type
  const isApprovalType = (priorityType) => {
    return ['Deal Approval', 'Approve UC', 'Approve CCR'].includes(priorityType);
  };

  // Process Priorities Data - separate approvals from other priorities
  // When no project filter: show each priority individually with project name, group approvals by project
  // When project filter applied: group priorities by title, group approvals by type
  const processPrioritiesData = (data) => {
    const hasProjectFilter = filters.projectID && filters.projectID !== '';

    // If no project filter
    if (!hasProjectFilter) {
      const prioritiesData = [];
      const approvalsByProject = {}; // Group approvals by project_id

      data.forEach((item) => {
        // Skip completed tasks
        if (item.status === 'Completed') return;

        const priorityType = item.type || 'Unknown';
        const priorityTitle = item.title || item.type || 'Unknown';

        if (isApprovalType(priorityType)) {
          // Group approvals by project_id
          const projectId = item.project_id || 'unknown';
          if (!approvalsByProject[projectId]) {
            approvalsByProject[projectId] = {
              projectId: projectId,
              projectName: item.project?.name || 'Unknown Project',
              items: []
            };
          }
          approvalsByProject[projectId].items.push(item);
        } else {
          // Show priorities individually
          prioritiesData.push({
            type: 'priority',
            priorityType: priorityType,
            priorityTitle: priorityTitle,
            count: 1,
            items: [item]
          });
        }
      });

      // Convert approvals grouped by project to display format
      const approvalsData = Object.values(approvalsByProject)
        .map((projectGroup) => {
          // Group items within each project by type
          const itemsByType = {};
          projectGroup.items.forEach((item) => {
            const priorityType = item.type || 'Unknown';
            if (!itemsByType[priorityType]) {
              itemsByType[priorityType] = [];
            }
            itemsByType[priorityType].push(item);
          });

          // Create approval entries for each type within the project
          return Object.entries(itemsByType).map(([priorityType, items]) => ({
            type: 'approval',
            priorityType: priorityType,
            priorityTitle: items[0]?.title || priorityType,
            count: items.length,
            items: items,
            projectId: projectGroup.projectId,
            projectName: projectGroup.projectName
          }));
        })
        .flat();

      return { priorities: prioritiesData, approvals: approvalsData };
    }

    // If project filter is applied
    // Priorities: group by title
    // Approvals: group by type (not title)
    const groupedByTitle = {};
    const approvalGroups = {};

    data.forEach((item) => {
      // Skip completed tasks
      if (item.status === 'Completed') return;

      const priorityType = item.type || 'Unknown';
      const priorityTitle = item.title || item.type || 'Unknown';

      // Separate approval types
      if (isApprovalType(priorityType)) {
        // Group approvals by type (not title)
        if (!approvalGroups[priorityType]) {
          approvalGroups[priorityType] = {
            type: priorityType,
            title: priorityTitle,
            count: 0,
            items: []
          };
        }
        approvalGroups[priorityType].count++;
        approvalGroups[priorityType].items.push(item);
      } else {
        // Regular priorities - group by title
        if (!groupedByTitle[priorityTitle]) {
          groupedByTitle[priorityTitle] = {
            type: priorityType,
            title: priorityTitle,
            count: 0,
            items: []
          };
        }
        groupedByTitle[priorityTitle].count++;
        groupedByTitle[priorityTitle].items.push(item);
      }
    });

    // Convert to display arrays
    const prioritiesData = [];
    const approvalsData = [];

    // Sort and add priorities
    Object.values(groupedByTitle)
      .sort((a, b) => b.count - a.count)
      .forEach((group) => {
        prioritiesData.push({
          type: 'priority',
          priorityType: group.type,
          priorityTitle: group.title,
          count: group.count,
          items: group.items
        });
      });

    // Add approvals (grouped by type) - no sorting
    Object.values(approvalGroups).forEach((group) => {
      approvalsData.push({
        type: 'approval',
        priorityType: group.type,
        priorityTitle: group.title,
        count: group.count,
        items: group.items
      });
    });

    return { priorities: prioritiesData, approvals: approvalsData };
  };

  return (
    <Layout>
      <Loader
        isVisible={
          isSubmitting ||
          isFiltersLoading ||
          isLoadingPriorities ||
          isLoadingManagementUsers ||
          isUpdatingProject ||
          isUpdatingPriority ||
          isLoadingSalesEscalations ||
          isLoadingEngineerEscalations ||
          isLoadingInventoryTasks
        }
      />

      <div
        className={`fixed top-0 z-10 transition-all duration-300 bg-white ${
          collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'
        }`}>
        <Header data={{ name: `Dashboard` }} showProjectSelector={false} />
        <DashboardFilters
          onFiltersChange={handleFiltersChange}
          onLoadingChange={handleFiltersLoadingChange}
          top="4rem"
          selectAllByDefault={true}
        />
      </div>

      {/* Body Content */}
      <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
        <div className="pt-[10rem] flex justify-flex-start items-start relative p-2 gap-7 pl-5">
          {/* Left Side - Configuration Cards */}
          <div
            className={`flex flex-col gap-5 transition-all duration-300 ease-in-out ${
              collapsed ? 'w-[63%]' : 'w-[59%]'
            }`}>
            <div className="w-full">
              <div className="grid grid-cols-1 gap-5">
                {/* Sales Card */}
                <div
                  className="flex  items-start gap-4 border-2 border-gray-200 rounded-xl p-6 cursor-pointer hover:bg-[#346C75]/10 hover:border-primary hover:shadow-lg transition-all duration-200 shadow-md bg-white min-h-[140px]"
                  onClick={() => {
                    navigate('/builder/management/configure', {
                      state: {
                        selectedCard: 'Sales',
                        projectID: filters.projectID,
                        month: filters.month
                      }
                    });
                    mixpanel.track('Configure Card Clicked', {
                      user_id: userID,
                      card_type: 'Sales',
                      project_id: filters.projectID,
                      timestamp: new Date().toISOString()
                    });
                  }}>
                  <div className="w-20 h-20 rounded-xl bg-primary flex items-center justify-center flex-shrink-0 shadow-md">
                    <img src={sales} alt="Sales" className="w-12 h-12 brightness-0 invert" />
                  </div>
                  <div className="flex-1 w-full">
                    <p className="text-xl font-bold text-gray-800 mb-1">Sales</p>
                    <p className="text-sm text-gray-600">
                      View sales reports and graphs to analyze performance and trends
                    </p>
                  </div>
                </div>

                {/* Operations Card */}
                <div
                  className="flex  items-start gap-4 border-2 border-gray-200 rounded-xl p-6 cursor-pointer hover:bg-[#346C75]/10 hover:border-primary hover:shadow-lg transition-all duration-200 shadow-md bg-white min-h-[140px]"
                  onClick={() => {
                    navigate('/builder/operations/dashboard', {
                      state: {
                        projectID: filters.projectID,
                        month: filters.month
                      }
                    });
                    mixpanel.track('Operations Card Clicked', {
                      user_id: userID,
                      card_type: 'Operations',
                      project_id: filters.projectID,
                      timestamp: new Date().toISOString()
                    });
                  }}>
                  <div className="w-20 h-20 rounded-xl bg-primary flex items-center justify-center flex-shrink-0 shadow-md">
                    <img
                      src={operations}
                      alt="Operations"
                      className="w-12 h-12 brightness-0 invert"
                    />
                  </div>
                  <div className="flex-1 w-full">
                    <p className="text-xl font-bold text-gray-800 mb-1">Operations</p>
                    <p className="text-sm text-gray-600">
                      View operations reports and graphs to track efficiency and productivity
                    </p>
                  </div>
                </div>

                {/* Inventory Card */}
                <div
                  className="flex  items-start gap-4 border-2 border-gray-200 rounded-xl p-6 cursor-pointer hover:bg-[#346C75]/10 hover:border-primary hover:shadow-lg transition-all duration-200 shadow-md bg-white min-h-[140px]"
                  onClick={() => {
                    navigate('/builder/inventory/dashboard', {
                      state: {
                        projectID: filters.projectID,
                        month: filters.month
                      }
                    });
                    mixpanel.track('Inventory Card Clicked', {
                      user_id: userID,
                      card_type: 'Inventory',
                      project_id: filters.projectID,
                      timestamp: new Date().toISOString()
                    });
                  }}>
                  <div className="w-20 h-20 rounded-xl bg-primary flex items-center justify-center flex-shrink-0 shadow-md">
                    <img
                      src={inventory}
                      alt="Inventory"
                      className="w-12 h-12 brightness-0 invert"
                    />
                  </div>
                  <div className="flex-1 w-full">
                    <p className="text-xl font-bold text-gray-800 mb-1">Inventory</p>
                    <p className="text-sm text-gray-600">
                      View inventory reports and graphs to monitor stock levels and usage
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Priorities, Approvals and Escalations */}
          <div className="h-[calc(100vh-11rem)] overflow-auto shadow flex flex-col bg-gray-100 w-[31%] fixed right-4">
            <div className="flex-1 overflow-y-auto rounded-lg bg-white p-4 shadow-lg">
              {/* Priorities Section */}
              <div className="mb-6 border border-gray-300 rounded-lg p-4">
                <h3 className="text-md font-semibold mb-4">Priorities</h3>
                {priorities && processPrioritiesData(priorities?.data).priorities.length > 0 ? (
                  processPrioritiesData(priorities?.data).priorities.map((item, index) => {
                    if (item.type === 'priority') {
                      // Use priorityTitle as display label
                      const displayLabel = item.priorityTitle || item.priorityType;

                      // Get project ID from first item in the group
                      const projectId = item.items?.[0]?.project_id || filters.projectID;

                      // Get project name from the first item (when no filter, each item has one project)
                      const projectName = item.items?.[0]?.project?.name;
                      const hasProjectFilter = filters.projectID && filters.projectID !== '';

                      return (
                        <div
                          key={`priority-${item.priorityTitle || item.priorityType}-${index}-${projectId}`}
                          className="flex items-center justify-between border border-gray-200 rounded-lg p-2 cursor-pointer hover:bg-gray-50 mb-2"
                          onClick={() => {
                            // Track priority item click
                            mixpanel.track('Priority Item Clicked', {
                              user_id: userID,
                              priority_type: item.priorityType,
                              count: item.count,
                              project_id: filters.projectID,
                              timestamp: new Date().toISOString()
                            });

                            // Check if this priority type requires modal
                            if (requiresModal(item.priorityType)) {
                              setSelectedPriorityType(item.priorityType);
                              setSelectedPriorityProjectId(projectId);
                              // Get the first priority ID from the items array
                              const priorityId = item.items?.[0]?.id || null;
                              setSelectedPriorityId(priorityId);
                              setShowPriorityModal(true);
                            } else {
                              // Use projectId from item when no filter is applied
                              const navProjectID = filters.projectID || projectId;

                              if (item.priorityType === 'Review Meeting') {
                                // Get the first priority ID from the items array
                                const priorityId = item.items?.[0]?.id || null;
                                navigate('/builder/management/review-meeting', {
                                  state: {
                                    projectID: navProjectID,
                                    month: filters.month,
                                    priorityId: priorityId
                                  }
                                });
                              } else if (item.priorityType === 'Set Targets') {
                                // Get the first priority ID from the items array
                                const priorityId = item.items?.[0]?.id || null;
                                navigate('/builder/management/set-target', {
                                  state: {
                                    projectID: navProjectID,
                                    month: filters.month,
                                    priorityId: priorityId
                                  }
                                });
                              } else {
                                // Navigate to Priorities page with type filter
                                navigate('/builder/management/priorities', {
                                  state: {
                                    priorityType: item.priorityType,
                                    projectID: navProjectID,
                                    selectedDate:
                                      localStorage.getItem('engineerSelectedDate') || selectedDate
                                  }
                                });
                              }
                            }
                          }}>
                          <div className="flex flex-col gap-1 flex-1">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-semibold">
                                {displayLabel.charAt(0).toUpperCase()}
                              </div>
                              <span className="text-sm font-medium text-gray-800">
                                {displayLabel}
                              </span>
                            </div>
                            {/* Show tag in next row for priorities only */}
                            <div className="flex items-center justify-start gap-1">
                              {item.items?.[0]?.tag &&
                                (() => {
                                  const tag = item.items[0].tag;
                                  let tagClasses =
                                    'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium';

                                  // Color coding based on tag type
                                  if (tag === 'Operations') {
                                    tagClasses +=
                                      ' bg-blue-100 text-blue-700 border border-blue-300';
                                  } else if (tag === 'Sales') {
                                    tagClasses +=
                                      ' bg-green-100 text-green-700 border border-green-300';
                                  } else if (tag === 'Management') {
                                    tagClasses +=
                                      ' bg-purple-100 text-purple-700 border border-purple-300';
                                  } else {
                                    tagClasses +=
                                      ' bg-gray-100 text-gray-700 border border-gray-300';
                                  }

                                  return (
                                    <div className="ml-8 mt-1">
                                      <span className={tagClasses}>{tag}</span>
                                    </div>
                                  );
                                })()}
                              {/* Show project name chip in next row when no project filter is applied */}
                              {!hasProjectFilter && projectName && (
                                <div className=" mt-1">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300">
                                    {projectName}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    }

                    return null;
                  })
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-500">No priorities available</p>
                  </div>
                )}

                {/* Escalations Header */}
                {(salesEscalations && getSalesEscalationsCount(salesEscalations?.data) > 0) ||
                (engineerEscalations &&
                  getEngineerEscalationsCount(engineerEscalations?.data) > 0) ||
                (inventoryTasks && getInventoryEscalationsCount(inventoryTasks) > 0) ? (
                  <>
                    <div className="mt-6 mb-4 border-t border-gray-300 pt-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Escalations</h4>
                    </div>

                    {/* Sales Escalations */}
                    {salesEscalations && getSalesEscalationsCount(salesEscalations?.data) > 0 && (
                      <div
                        className="flex items-center justify-between border border-gray-200 rounded-lg p-2 cursor-pointer hover:bg-gray-50 mb-2"
                        onClick={() => {
                          mixpanel.track('Sales Escalation Clicked', {
                            user_id: userID,
                            count: getSalesEscalationsCount(salesEscalations?.data),
                            project_id: filters.projectID,
                            timestamp: new Date().toISOString()
                          });

                          navigate('/builder/management/priorities', {
                            state: {
                              escalationType: 'salesEscalations',
                              projectID: filters.projectID,
                              selectedDate: selectedDate
                            }
                          });
                        }}>
                        <div className="flex items-center gap-2 flex-1">
                          <div className="w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center text-xs font-semibold">
                            E
                          </div>
                          <span className="text-sm font-medium text-gray-800">
                            Sales Escalations | {getSalesEscalationsCount(salesEscalations?.data)}{' '}
                            {getSalesEscalationsCount(salesEscalations?.data) === 1
                              ? 'task'
                              : 'tasks'}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Engineer Escalations */}
                    {engineerEscalations &&
                      getEngineerEscalationsCount(engineerEscalations?.data) > 0 && (
                        <div
                          className="flex items-center justify-between border border-gray-200 rounded-lg p-2 cursor-pointer hover:bg-gray-50 mb-2"
                          onClick={() => {
                            mixpanel.track('Engineer Escalation Clicked', {
                              user_id: userID,
                              count: getEngineerEscalationsCount(engineerEscalations?.data),
                              project_id: filters.projectID,
                              timestamp: new Date().toISOString()
                            });

                            navigate('/builder/management/priorities', {
                              state: {
                                escalationType: 'engineerEscalations',
                                projectID: filters.projectID,
                                selectedDate: selectedDate
                              }
                            });
                          }}>
                          <div className="flex items-center gap-2 flex-1">
                            <div className="w-6 h-6 rounded-full bg-orange-500 text-white flex items-center justify-center text-xs font-semibold">
                              E
                            </div>
                            <span className="text-sm font-medium text-gray-800">
                              Engineer Escalations |{' '}
                              {getEngineerEscalationsCount(engineerEscalations?.data)}{' '}
                              {getEngineerEscalationsCount(engineerEscalations?.data) === 1
                                ? 'task'
                                : 'tasks'}
                            </span>
                          </div>
                        </div>
                      )}

                    {/* Inventory Escalations */}
                    {inventoryTasks && getInventoryEscalationsCount(inventoryTasks) > 0 && (
                      <div
                        className="flex items-center justify-between border border-gray-200 rounded-lg p-2 cursor-pointer hover:bg-gray-50 mb-2"
                        onClick={() => {
                          mixpanel.track('Inventory Escalation Clicked', {
                            user_id: userID,
                            count: getInventoryEscalationsCount(inventoryTasks),
                            project_id: filters.projectID,
                            timestamp: new Date().toISOString()
                          });

                          navigate('/builder/management/priorities', {
                            state: {
                              escalationType: 'inventoryEscalations',
                              projectID: filters.projectID,
                              selectedDate: selectedDate
                            }
                          });
                        }}>
                        <div className="flex items-center gap-2 flex-1">
                          <div className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-xs font-semibold">
                            E
                          </div>
                          <span className="text-sm font-medium text-gray-800">
                            Inventory Escalations | {getInventoryEscalationsCount(inventoryTasks)}{' '}
                            {getInventoryEscalationsCount(inventoryTasks) === 1 ? 'task' : 'tasks'}
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                ) : null}
              </div>

              {/* Approvals Section */}
              <div className="mb-6 border border-gray-300 rounded-lg p-4">
                <h3 className="text-md font-semibold mb-4">Approvals</h3>
                {priorities && processPrioritiesData(priorities?.data).approvals.length > 0 ? (
                  processPrioritiesData(priorities?.data).approvals.map((item, index) => {
                    if (item.type === 'approval') {
                      const hasProjectFilter = filters.projectID && filters.projectID !== '';

                      // When project filter is applied (grouped by type), use display label mapping
                      // When no filter (grouped by project), use display label mapping with priorityTitle
                      let displayLabel;
                      if (hasProjectFilter) {
                        // Grouped by type - use display label mapping
                        if (item.priorityType === 'Deal Approval') {
                          displayLabel = 'Approve Closed Deal';
                        } else if (item.priorityType === 'Approve UC') {
                          displayLabel = 'Approve Under Consideration';
                        } else if (item.priorityType === 'Approve CCR') {
                          displayLabel = 'Approve Customer Change Request';
                        } else {
                          displayLabel = item.priorityType;
                        }
                      } else {
                        // Grouped by project - use display label mapping
                        if (item.priorityType === 'Deal Approval') {
                          displayLabel = 'Approve Closed Deal';
                        } else if (item.priorityType === 'Approve UC') {
                          displayLabel = 'Approve Under Consideration';
                        } else if (item.priorityType === 'Approve CCR') {
                          displayLabel = 'Approve Customer Change Request';
                        } else {
                          displayLabel = item.priorityTitle || item.priorityType;
                        }
                      }

                      // Get project ID from item or first item in the group
                      const projectId =
                        item.projectId || item.items?.[0]?.project_id || filters.projectID;

                      // Get project name from item (when grouped by project) or first item
                      const projectName = item.projectName || item.items?.[0]?.project?.name;

                      return (
                        <div
                          key={`approval-${hasProjectFilter ? item.priorityType : `${item.priorityType}-${projectId}`}-${index}`}
                          className="flex items-center justify-between border border-gray-200 rounded-lg p-2 cursor-pointer hover:bg-gray-50 mb-2"
                          onClick={() => {
                            // Track approval item click
                            mixpanel.track('Approval Item Clicked', {
                              user_id: userID,
                              approval_type: item.priorityType,
                              count: item.count,
                              project_id: filters.projectID,
                              timestamp: new Date().toISOString()
                            });

                            // Navigate to Priorities page with type filter
                            navigate('/builder/management/priorities', {
                              state: {
                                priorityType: item.priorityType,
                                projectID: filters.projectID || projectId,
                                selectedDate:
                                  localStorage.getItem('engineerSelectedDate') || selectedDate
                              }
                            });
                          }}>
                          <div className="flex flex-col gap-1 flex-1">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full bg-purple-500 text-white flex items-center justify-center text-xs font-semibold">
                                {displayLabel.charAt(0).toUpperCase()}
                              </div>
                              <span className="text-sm font-medium text-gray-800">
                                {displayLabel} | {item.count} {item.count === 1 ? 'task' : 'tasks'}
                              </span>
                            </div>
                            {/* Show project name chip in next row when no project filter is applied */}
                            {!hasProjectFilter && projectName && (
                              <div className="ml-8 mt-1">
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300">
                                  {projectName}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    }

                    return null;
                  })
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-500">No approvals available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Priority Assignment Modal */}
      {showPriorityModal && (
        <Modal
          name={
            selectedPriorityType === 'Review Meeting Date' ||
            selectedPriorityType === 'Target Meeting Date'
              ? `Assign ${selectedPriorityType}`
              : `Assign User for ${selectedPriorityType}`
          }
          width={50}
          onClose={() => {
            setShowPriorityModal(false);
            resetPriority();
            setSelectedPriorityType(null);
            setSelectedPriorityProjectId(null);
            setSelectedPriorityId(null);
          }}>
          <form onSubmit={handleSubmitPriority(onSubmitPriorityAssignment)}>
            {selectedPriorityType === 'Review Meeting Date' ||
            selectedPriorityType === 'Target Meeting Date' ? (
              // Calendar for day selection (1-28)
              <div className="space-y-4">
                <div
                  className="flex-shrink-0 flex flex-col gap-2 text-gray-700"
                  style={{ width: '100%' }}>
                  <div className="flex justify-between items-start space-x-2 w-fit">
                    <span className="text-gray-600">Day of Month</span>
                    <span className="text-red-500">*</span>
                  </div>
                  <Controller
                    name="day"
                    control={controlPriority}
                    rules={{
                      required: 'Day is required',
                      validate: (value) => {
                        if (!value) return 'Day is required';
                        const day = parseInt(value, 10);
                        if (isNaN(day) || day < 1 || day > 28) {
                          return 'Day must be a number between 1 and 28';
                        }
                        return true;
                      }
                    }}
                    render={({ field }) => {
                      const selectedDay = field.value ? parseInt(field.value, 10) : null;
                      const showWarning = selectedDay && selectedDay > 5 && selectedDay <= 28;

                      // Calculate which day of the week the 1st of current month falls on
                      const today = new Date();
                      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                      const startingDayOfWeek = firstDayOfMonth.getDay(); // 0 = Sunday, 1 = Monday, etc.

                      // Generate calendar cells
                      const calendarCells = [];

                      // Add empty cells for days before the 1st
                      for (let i = 0; i < startingDayOfWeek; i++) {
                        calendarCells.push({ day: null, isEmpty: true });
                      }

                      // Add days 1-28
                      for (let day = 1; day <= 28; day++) {
                        calendarCells.push({ day, isEmpty: false });
                      }

                      return (
                        <div>
                          {/* Calendar Grid */}
                          <div className="grid grid-cols-7 gap-2 p-4 border border-gray-200 rounded-lg bg-white">
                            {/* Day headers */}
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((dayName) => (
                              <div
                                key={dayName}
                                className="text-center text-xs font-semibold text-gray-600 py-1.5">
                                {dayName}
                              </div>
                            ))}

                            {/* Calendar cells */}
                            {calendarCells.map((cell, index) => {
                              if (cell.isEmpty) {
                                return <div key={`empty-${index}`} className="h-10" />;
                              }

                              const day = cell.day;
                              const isSelected = selectedDay === day;
                              const isOptimal = day >= 1 && day <= 5;
                              const isNonOptimal = day > 5 && day <= 28;

                              return (
                                <button
                                  key={day}
                                  type="button"
                                  onClick={() => {
                                    field.onChange(day.toString());
                                  }}
                                  className={`
                                    h-10 w-full rounded-lg text-xs font-medium transition-all
                                    flex items-center justify-center
                                    ${
                                      isSelected
                                        ? 'bg-primary text-white shadow-md ring-2 ring-primary'
                                        : isOptimal
                                          ? 'bg-white text-gray-900 hover:bg-gray-100 border border-gray-300'
                                          : 'bg-white text-gray-600 hover:bg-yellow-50 border border-yellow-300'
                                    }
                                  `}>
                                  {day}
                                </button>
                              );
                            })}
                          </div>
                          {/* Warning message for dates outside optimal range */}
                          {showWarning && (
                            <p className="text-yellow-600 text-sm mt-3 flex items-center gap-1">
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                />
                              </svg>
                              Optimal date for the meeting is from 1 to 5 of the month
                            </p>
                          )}
                        </div>
                      );
                    }}
                  />
                  {priorityErrors?.day && (
                    <p className="text-red-500 text-sm mt-1">{priorityErrors.day.message}</p>
                  )}
                </div>
                <p className="text-sm text-gray-500">
                  Select the day of the month (1-28) for the{' '}
                  {selectedPriorityType === 'Review Meeting Date'
                    ? 'review meeting'
                    : 'target meeting'}
                </p>

                {/* Organizer User and Attendees fields - for both Review Meeting Date and Target Meeting Date */}
                {(selectedPriorityType === 'Review Meeting Date' ||
                  selectedPriorityType === 'Target Meeting Date') && (
                  <div className="mt-4 space-y-4">
                    {isLoadingManagementUsers ? (
                      <p className="text-sm text-gray-500">Loading users...</p>
                    ) : (
                      <>
                        <FormInput
                          width={100}
                          label="Organizer User"
                          register={registerPriority}
                          register_key={
                            selectedPriorityType === 'Review Meeting Date'
                              ? 'organizerUserId'
                              : 'targetOrganizerUserId'
                          }
                          type="dropdown"
                          options={
                            managementUsers?.data?.map((user) => ({
                              value: user.id,
                              label: `${user.name}${user.vertical ? ` - ${user.vertical}` : ''}${user.level ? ` (${user.level})` : ''}`
                            })) || []
                          }
                          isRequired={true}
                          validation={{ required: 'Organizer user is required' }}
                          errors={priorityErrors}
                        />
                        <Controller
                          name={
                            selectedPriorityType === 'Review Meeting Date'
                              ? 'attendees'
                              : 'targetAttendees'
                          }
                          control={controlPriority}
                          defaultValue={[]}
                          rules={{
                            required: 'At least one attendee is required',
                            validate: (value) => {
                              if (!value || !Array.isArray(value) || value.length === 0) {
                                return 'At least one attendee is required';
                              }
                              return true;
                            }
                          }}
                          render={({ field, fieldState }) => (
                            <div
                              className="flex-shrink-0 flex flex-col gap-2 text-gray-600"
                              style={{ width: '100%' }}>
                              <div className="flex justify-between items-start space-x-2 w-fit">
                                <span className="text-gray-600">Select Meeting Attendees</span>
                                <span className="text-red-500">*</span>
                              </div>
                              <Multiselect
                                options={
                                  managementUsers?.data?.map((user) => ({
                                    id: user.id,
                                    name: `${user.name}${user.vertical ? ` - ${user.vertical}` : ''}${user.level ? ` (${user.level})` : ''}`
                                  })) || []
                                }
                                selectedValues={field.value || []}
                                onSelect={(selectedList) => {
                                  field.onChange(selectedList);
                                }}
                                onRemove={(selectedList) => {
                                  field.onChange(selectedList);
                                }}
                                displayValue="name"
                                placeholder="Select attendees"
                                style={{
                                  optionContainer: {
                                    border: '1px solid',
                                    backgroundColor: 'white'
                                  },
                                  option: {
                                    color: 'black',
                                    backgroundColor: 'white'
                                  },
                                  chips: {
                                    background: '#2A565E',
                                    color: 'white'
                                  }
                                }}
                              />
                              {fieldState.error && (
                                <p className="text-red-500 text-sm mt-1">
                                  {fieldState.error.message}
                                </p>
                              )}
                            </div>
                          )}
                        />
                      </>
                    )}
                  </div>
                )}
              </div>
            ) : (
              // User dropdown
              <div className="space-y-4">
                {isLoadingManagementUsers ? (
                  <p className="text-sm text-gray-500">Loading users...</p>
                ) : (
                  <FormInput
                    width={100}
                    label="Select User"
                    register={registerPriority}
                    register_key="userId"
                    type="dropdown"
                    options={
                      managementUsers?.data?.map((user) => ({
                        value: user.id,
                        label: `${user.name}${user.vertical ? ` - ${user.vertical}` : ''}${user.level ? ` (${user.level})` : ''}`
                      })) || []
                    }
                    isRequired={true}
                    validation={{ required: 'User is required' }}
                    errors={priorityErrors}
                  />
                )}
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <Button
                type="button"
                displayType="secondary"
                onClick={() => {
                  setShowPriorityModal(false);
                  resetPriority();
                  setSelectedPriorityType(null);
                  setSelectedPriorityProjectId(null);
                  setSelectedPriorityId(null);
                }}>
                Cancel
              </Button>
              <Button type="submit" disabled={isUpdatingProject || isUpdatingPriority}>
                {isUpdatingProject || isUpdatingPriority ? 'Updating...' : 'Assign'}
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </Layout>
  );
};

export default ManagementDashboard;
